import React, { useState } from "react";
import { useLang } from "@/lib/i18n";
import { submitForm } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";

const SOURCE_KEYS = ["google", "chatgpt", "linkedin", "facebook", "transport", "other"];

const ContactForm = ({ defaultType = "contact", title, subtitle, hideTypeSelector = false, dataTestid = "contact-form" }) => {
  const { t, lang } = useLang();
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    fleet_size: "",
    message: "",
    location: "",
    source: "",
    source_other: "",
    type: defaultType,
  });

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    // Client-side guard: ensure required new fields are filled.
    if (!form.source) {
      toast.error(lang === "nl" ? "Kies hoe u ons heeft gevonden." : "Please select how you found us.");
      return;
    }
    if (form.source === "other" && !form.source_other.trim()) {
      toast.error(lang === "nl" ? "Vul a.u.b. toe hoe u ons heeft gevonden." : "Please specify how you found us.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        locale: lang,
        // Only send source_other when relevant to keep payload clean.
        source_other: form.source === "other" ? form.source_other.trim() : null,
      };
      // Backend stores the lead in MongoDB and sends the notification email via Resend.
      await submitForm(payload);
      setSuccess(true);
      toast.success(t("common.success"));
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        fleet_size: "",
        message: "",
        location: "",
        source: "",
        source_other: "",
        type: defaultType,
      });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      let msg = t("common.error");
      if (Array.isArray(detail) && detail.length > 0) {
        const fieldLabels = {
          name: lang === "nl" ? "Naam" : "Name",
          email: "E-mail",
          phone: lang === "nl" ? "Telefoonnummer" : "Phone",
          company: lang === "nl" ? "Bedrijfsnaam" : "Company",
          fleet_size: lang === "nl" ? "Wagenpark grootte" : "Fleet size",
          message: lang === "nl" ? "Bericht" : "Message",
          location: lang === "nl" ? "Locatie" : "Location",
          source: lang === "nl" ? "Hoe gevonden" : "How found",
          source_other: lang === "nl" ? "Toelichting" : "Details",
        };
        msg = detail
          .map((d) => {
            const field = (d.loc || []).slice(-1)[0];
            const label = fieldLabels[field] || field;
            if (d.type === "string_too_short") {
              const min = d.ctx?.min_length;
              return lang === "nl"
                ? `${label}: minimaal ${min} tekens`
                : `${label}: at least ${min} characters`;
            }
            if (d.type === "string_too_long") {
              const max = d.ctx?.max_length;
              return lang === "nl"
                ? `${label}: maximaal ${max} tekens`
                : `${label}: max ${max} characters`;
            }
            if (d.type === "value_error" || d.type === "email" || d.type?.includes("email")) {
              return lang === "nl" ? `${label}: ongeldig formaat` : `${label}: invalid format`;
            }
            return `${label}: ${d.msg || "invalid"}`;
          })
          .join(" · ");
      } else if (typeof detail === "string") {
        msg = detail;
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <div data-testid={`${dataTestid}-success`} className="rounded-2xl border border-[#10B981]/30 bg-[#10B981]/5 p-10 text-center">
        <div className="w-14 h-14 rounded-full bg-[#10B981]/15 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-7 h-7 text-[#10B981]" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">{lang === "nl" ? "Aanvraag ontvangen" : "Request received"}</h3>
        <p className="text-white/60 max-w-sm mx-auto">{t("common.success")}</p>
        <Button
          data-testid={`${dataTestid}-reset`}
          onClick={() => setSuccess(false)}
          variant="outline"
          className="mt-6 border-white/15 text-white hover:bg-white/5"
        >
          {lang === "nl" ? "Nog een aanvraag indienen" : "Submit another request"}
        </Button>
      </div>
    );
  }

  return (
    <form data-testid={dataTestid} onSubmit={onSubmit} className="rounded-2xl border border-white/10 bg-[#0F0F23] p-8 sm:p-10 space-y-5">
      {title && <h3 className="text-2xl font-bold text-white tracking-tight">{title}</h3>}
      {subtitle && <p className="text-white/55 text-sm -mt-3">{subtitle}</p>}

      {!hideTypeSelector && (
        <div className="grid grid-cols-3 gap-2 p-1 bg-[#050509] border border-white/10 rounded-lg">
          {[
            ["quote", t("form.type_quote")],
            ["demo", t("form.type_demo")],
            ["contact", t("form.type_contact")],
          ].map(([val, label]) => (
            <button
              type="button"
              key={val}
              data-testid={`${dataTestid}-type-${val}`}
              onClick={() => setForm((f) => ({ ...f, type: val }))}
              className={`text-[12px] font-semibold py-2 rounded-md transition-colors ${
                form.type === val ? "bg-[#00D4FF] text-[#050509]" : "text-white/60 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-white/70 text-xs uppercase tracking-wider">
            {t("form.name")} *
          </Label>
          <Input
            id="name"
            data-testid={`${dataTestid}-name`}
            required
            value={form.name}
            onChange={onChange("name")}
            className="bg-[#050509] border-white/10 text-white h-11 focus-visible:ring-[#00D4FF]"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-white/70 text-xs uppercase tracking-wider">
            {t("form.email")} *
          </Label>
          <Input
            id="email"
            type="email"
            data-testid={`${dataTestid}-email`}
            required
            value={form.email}
            onChange={onChange("email")}
            className="bg-[#050509] border-white/10 text-white h-11 focus-visible:ring-[#00D4FF]"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-white/70 text-xs uppercase tracking-wider">
            {t("form.phone")}
          </Label>
          <Input
            id="phone"
            data-testid={`${dataTestid}-phone`}
            value={form.phone}
            onChange={onChange("phone")}
            className="bg-[#050509] border-white/10 text-white h-11 focus-visible:ring-[#00D4FF]"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="company" className="text-white/70 text-xs uppercase tracking-wider">
            {t("form.company")}
          </Label>
          <Input
            id="company"
            data-testid={`${dataTestid}-company`}
            value={form.company}
            onChange={onChange("company")}
            className="bg-[#050509] border-white/10 text-white h-11 focus-visible:ring-[#00D4FF]"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="location" className="text-white/70 text-xs uppercase tracking-wider">
            {t("form.location")} *
          </Label>
          <Input
            id="location"
            data-testid={`${dataTestid}-location`}
            required
            placeholder={t("form.location_placeholder")}
            value={form.location}
            onChange={onChange("location")}
            className="bg-[#050509] border-white/10 text-white h-11 focus-visible:ring-[#00D4FF]"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="source" className="text-white/70 text-xs uppercase tracking-wider">
            {t("form.source")} *
          </Label>
          <Select
            value={form.source}
            onValueChange={(val) => setForm((f) => ({ ...f, source: val, source_other: val === "other" ? f.source_other : "" }))}
          >
            <SelectTrigger
              id="source"
              data-testid={`${dataTestid}-source`}
              className="bg-[#050509] border-white/10 text-white h-11 focus:ring-[#00D4FF]"
            >
              <SelectValue placeholder={t("form.source_placeholder")} />
            </SelectTrigger>
            <SelectContent className="bg-[#0F0F23] border-white/10 text-white">
              {SOURCE_KEYS.map((key) => (
                <SelectItem
                  key={key}
                  value={key}
                  data-testid={`${dataTestid}-source-option-${key}`}
                  className="focus:bg-white/10 focus:text-white"
                >
                  {t(`form.source_options.${key}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {form.source === "other" && (
        <div className="space-y-2">
          <Label htmlFor="source_other" className="text-white/70 text-xs uppercase tracking-wider">
            {t("form.source_other_label")} *
          </Label>
          <Input
            id="source_other"
            data-testid={`${dataTestid}-source-other`}
            required
            placeholder={t("form.source_other_placeholder")}
            value={form.source_other}
            onChange={onChange("source_other")}
            className="bg-[#050509] border-white/10 text-white h-11 focus-visible:ring-[#00D4FF]"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="fleet" className="text-white/70 text-xs uppercase tracking-wider">
          {t("form.fleet_size")}
        </Label>
        <Input
          id="fleet"
          data-testid={`${dataTestid}-fleet`}
          placeholder={lang === "nl" ? "Bijv. 25 vrachtwagens" : "E.g. 25 trucks"}
          value={form.fleet_size}
          onChange={onChange("fleet_size")}
          className="bg-[#050509] border-white/10 text-white h-11 focus-visible:ring-[#00D4FF]"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="text-white/70 text-xs uppercase tracking-wider">
          {t("form.message")} *
        </Label>
        <Textarea
          id="message"
          data-testid={`${dataTestid}-message`}
          required
          rows={5}
          value={form.message}
          onChange={onChange("message")}
          className="bg-[#050509] border-white/10 text-white focus-visible:ring-[#00D4FF]"
        />
      </div>

      <Button
        type="submit"
        data-testid={`${dataTestid}-submit`}
        disabled={busy}
        className="w-full bg-[#00D4FF] hover:bg-white text-[#050509] font-bold h-12 text-sm"
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> {t("common.sending")}
          </>
        ) : (
          <>
            {t("common.send")} <ArrowRight className="w-4 h-4" />
          </>
        )}
      </Button>

      <p className="text-[11px] text-white/30 text-center">
        {lang === "nl"
          ? "We reageren binnen 1 werkdag. Geen spam, alleen een persoonlijke terugkoppeling."
          : "We respond within one business day. No spam, just a personal reply."}
      </p>
    </form>
  );
};

export default ContactForm;
